import { defineStore } from "pinia";
import { computed, ref, watch } from "vue";

import {
  readNotificationPreferences,
  writeNotificationPreferences,
} from "@/cache/notificationPreferences";
import { useUserStore } from "@/stores/user";
import type { PagePhase } from "@/types/candidatePages";

const SIGN_IN_REQUIRED_MESSAGE = "Sign in is required to view this account.";

function errorMessage(caught: unknown): string {
  return caught instanceof Error && caught.message
    ? caught.message
    : "The account could not be loaded.";
}

function isAbortError(caught: unknown): boolean {
  return caught instanceof DOMException && caught.name === "AbortError";
}

export const useAccountStore = defineStore("account", () => {
  const userStore = useUserStore();
  const preferences = readNotificationPreferences();
  const phase = ref<PagePhase>("idle");
  const error = ref("");
  const hasLoaded = ref(false);
  const wishlistAlerts = ref(preferences.wishlistAlerts);
  const statusReminders = ref(preferences.statusReminders);
  const marketingMessages = ref(preferences.marketingMessages);
  let activeController: AbortController | null = null;
  let generation = 0;

  watch([wishlistAlerts, statusReminders, marketingMessages], ([wishlist, reminders, marketing]) => {
    writeNotificationPreferences({
      marketingMessages: marketing,
      statusReminders: reminders,
      wishlistAlerts: wishlist,
    });
  }, { flush: "sync" });

  function savePreferences(): void {
    writeNotificationPreferences({
      marketingMessages: marketingMessages.value,
      statusReminders: statusReminders.value,
      wishlistAlerts: wishlistAlerts.value,
    });
  }

  const busy = computed(() => phase.value === "loading" || phase.value === "updating");
  const accountStatus = computed(() => {
    if (phase.value === "loading") return "Loading account";
    if (phase.value === "updating") return "Refreshing account";
    if (phase.value === "error") {
      return error.value === SIGN_IN_REQUIRED_MESSAGE ? "Sign in required" : "Account unavailable";
    }
    return hasLoaded.value ? "Loaded" : "Waiting";
  });

  async function load(): Promise<void> {
    activeController?.abort();
    const controller = new AbortController();
    activeController = controller;
    const requestGeneration = ++generation;
    phase.value = hasLoaded.value ? "updating" : "loading";
    error.value = "";
    try {
      const user = await userStore.ensureSession();
      if (activeController !== controller || generation !== requestGeneration || controller.signal.aborted) return;
      if (!user) throw new Error(SIGN_IN_REQUIRED_MESSAGE);
      hasLoaded.value = true;
      phase.value = "success";
    } catch (caught: unknown) {
      if (activeController !== controller || generation !== requestGeneration || isAbortError(caught)) return;
      error.value = errorMessage(caught);
      phase.value = hasLoaded.value ? "success" : "error";
    } finally {
      if (activeController === controller) activeController = null;
    }
  }

  function dispose(): void {
    generation += 1;
    activeController?.abort();
    activeController = null;
    if (phase.value === "updating") phase.value = hasLoaded.value ? "success" : "idle";
  }

  function reset(): void {
    generation += 1;
    activeController?.abort();
    activeController = null;
    phase.value = "idle";
    error.value = "";
    hasLoaded.value = false;
  }

  return {
    accountStatus,
    busy,
    dispose,
    error,
    hasLoaded,
    load,
    marketingMessages,
    phase,
    reset,
    savePreferences,
    statusReminders,
    wishlistAlerts,
  };
});
